import React from 'react';

/**
 * <ScoreGauge score={82} color="var(--threat-critical)" label="HIGH RISK" />
 *
 * Circular risk gauge. Stroke color is the severity color.
 */
export default function ScoreGauge({
  score = 0,
  color = 'var(--accent)',
  label,
  bg,
  border,
  size = 128,
  style,
  ...rest
}) {
  const r = size * 0.4;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score || 0));
  const dash = (clamped / 100) * circ;

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--sp-3)',
        flexShrink: 0, ...style,
      }}
      {...rest}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id={`glow-${size}`}>
            <feGaussianBlur stdDeviation="3"/>
            <feComposite in="SourceGraphic"/>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={r}
          fill="none" stroke="var(--bg-elevated)" strokeWidth="10"/>
        <circle cx={cx} cy={cy} r={r}
          fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          opacity="0.25"
          style={{ filter: `url(#glow-${size})` }}
        />
        <circle cx={cx} cy={cy} r={r}
          fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 0.8s var(--ease-out), stroke 0.3s var(--ease-out)' }}
        />
        <text x={cx} y={cy - 4} textAnchor="middle"
          fill={color} fontSize={size * 0.22} fontWeight="700"
          fontFamily="var(--font-mono)">
          {score ?? 0}
        </text>
        <text x={cx} y={cy + size * 0.12} textAnchor="middle"
          fill="var(--text-muted)" fontSize={size * 0.08}
          fontFamily="var(--font-mono)">
          / 100
        </text>
      </svg>
      {label && (
        <div className="score-gauge-label" style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)',
          letterSpacing: 'var(--ls-wide)',
          padding: '4px 12px', borderRadius: 'var(--radius-sm)',
          color, background: bg || `color-mix(in srgb, ${color} 10%, transparent)`,
          border: border || `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
          textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </div>
      )}
    </div>
  );
}
